import { useEffect, useState, useCallback, useRef, useMemo, type Ref } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AudioTrack,
  LiveKitRoom,
  useParticipants,
  useLocalParticipant,
  useTracks,
} from '@livekit/components-react';
import '@livekit/components-styles';
import { Track, type MediaDeviceFailure, type RoomOptions } from 'livekit-client';
import { useAuth } from '../../auth';
import type { PoseTimerState, Room, ScheduleItem, SessionArtwork } from '@modelcast/types';
import { RoomProvider, useRoom } from './RoomProvider';
import { FeaturedVideo } from '../video/FeaturedVideo';
import { StageParticipantGrid } from '../video/StageParticipantGrid';
import { MediaControls } from '../video/MediaControls';
import { ModelFrameStreamer } from '../video/ModelFrameStreamer';
import { LocalCameraPublishProfile } from '../video/LocalCameraPublishProfile';
import { RoomDiagnosticsOverlay } from '../video/RoomDiagnosticsOverlay';
import { LiveKitDiagnostics } from '../video/LiveKitDiagnostics';
import { BrowserSourceRecorder } from '../video/BrowserSourceRecorder';
import { ParticipantStrip } from './ParticipantStrip';
import { PoseSchedulePanel } from './PoseSchedulePanel';
import { SidebarPanel, type SidebarTab } from './SidebarPanel';
import { DeviceSelector } from '../settings/DeviceSelector';
import { UploadArtworkModal } from './UploadArtworkModal';
import { SessionHeader } from './SessionHeader';
import { NewPoseModal } from './NewPoseModal';
import { usePoseTimer, usePoseControls, usePoseSoundEffects } from './hooks';
import { AudioDebugPanel } from '../video/AudioDebugPanel';
import { HostControlsPanel } from './HostControlsPanel';
import { PinnedArtworkOverlay } from './PinnedArtworkOverlay';
import { DEFAULT_HI_RES_TUNING_SETTINGS } from '../video/HiResTuningPanel';
import type { HiResTuningSettings } from '../video/HiResTuningPanel';
import { logVideoDiagnostic } from '../video/videoDiagnostics';
import { getPreferredCameraDeviceId, getPreferredMicrophoneDeviceId } from '../video/cameraDevicePreference';
import { applyArtworkFavoriteState } from './artworkFavorites';
import {
  getLiveKitRoomOptions,
  isActiveModelCameraParticipant,
  type LiveKitCameraProfile,
} from '../video/liveKitCameraProfiles';
import {
  createMediaDeviceFailureError,
  getMediaAccessIssue,
  type MediaAccessIssue,
} from './mediaAccess';
import {
  clearStoredRoomSession,
  getRoomSessionDiagnosticContext,
  updateStoredRoomSessionFromAccess,
} from './joinSession';
import { uploadRoomArtwork } from './uploadRoomArtwork';

const CONDUCT_AGREEMENT_STORAGE_PREFIX = 'drawclub.conductAccepted';
const FULLSCREEN_POSE_CONTROLS_IDLE_MS = 1800;

type LiveKitParticipant = ReturnType<typeof useParticipants>[number];
type RoomFullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

function getRoomFullscreenElement() {
  if (typeof document === 'undefined') return null;
  const fullscreenDocument = document as RoomFullscreenDocument;
  return document.fullscreenElement ?? fullscreenDocument.webkitFullscreenElement ?? null;
}

async function exitRoomDocumentFullscreen() {
  if (!getRoomFullscreenElement()) return;

  try {
    const fullscreenDocument = document as RoomFullscreenDocument;
    const exitFullscreen = document.exitFullscreen ?? fullscreenDocument.webkitExitFullscreen;
    await exitFullscreen?.call(document);
  } catch (error) {
    console.warn('[RoomPage] fullscreen exit failed', error);
  }
}

function hasActiveCameraPublication(participant: LiveKitParticipant) {
  const cameraPublication = participant.getTrackPublication(Track.Source.Camera);
  return participant.isCameraEnabled && cameraPublication?.isMuted !== true;
}

function getConductAgreementKey(roomCode: string, participantId: string | null) {
  return `${CONDUCT_AGREEMENT_STORAGE_PREFIX}:${roomCode}:${participantId ?? 'unknown'}`;
}

function hasAcceptedConductAgreement(roomCode: string, participantId: string | null) {
  try {
    return localStorage.getItem(getConductAgreementKey(roomCode, participantId)) === 'true';
  } catch {
    return false;
  }
}

function markConductAgreementAccepted(roomCode: string, participantId: string | null) {
  try {
    localStorage.setItem(getConductAgreementKey(roomCode, participantId), 'true');
  } catch {
    // Keep the session usable if localStorage is unavailable.
  }
}

function shouldRequireConductAgreement(roomCode: string, conductAccepted: boolean) {
  if (conductAccepted) return false;

  const participantRole = sessionStorage.getItem(`participant-role-${roomCode}`);
  const participantIsHost = sessionStorage.getItem(`participant-isHost-${roomCode}`) === 'true';

  return participantRole === 'artist' && !participantIsHost;
}

export function RoomPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [token, setToken] = useState<string | null>(null);
  const [serverUrl, setServerUrl] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [room, setRoom] = useState<Room | null>(null);
  const [isEndingSession, setIsEndingSession] = useState(false);
  const [connectWithMedia, setConnectWithMedia] = useState(true);
  const [liveKitRoomKey, setLiveKitRoomKey] = useState(0);
  const [mediaAccessIssue, setMediaAccessIssue] = useState<MediaAccessIssue | null>(null);
  const [preferredCameraDeviceId] = useState(() => getPreferredCameraDeviceId());
  const [preferredMicrophoneDeviceId] = useState(() => getPreferredMicrophoneDeviceId());
  const [conductAccepted, setConductAccepted] = useState(false);
  const mediaFallbackStartedRef = useRef(false);

  useEffect(() => {
    if (!code) {
      navigate('/');
      return;
    }

    const roomCode = code;
    let cancelled = false;

    async function loadRoomSession() {
      // Get token from session storage - role/host state comes from RoomProvider context
      const storedToken = sessionStorage.getItem(`room-token-${roomCode}`);

      if (!storedToken) {
        navigate(`/s/${roomCode}/join`, { replace: true });
        return;
      }

      try {
        const authToken = await getToken();
        const response = await fetch(`/api/rooms/${roomCode}/access`, {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        });
        const data = await response.json().catch(() => ({}));

        if (cancelled) return;

        if (data.requiresInviteAcceptance && data.invitationUrl) {
          clearStoredRoomSession(roomCode);
          navigate(data.invitationUrl, { replace: true });
          return;
        }

        if (data.requiresTicket) {
          clearStoredRoomSession(roomCode);
          navigate(data.ticketUrl || `/s/${roomCode}`, { replace: true });
          return;
        }

        if (data.sessionNotOpen) {
          clearStoredRoomSession(roomCode);
          navigate(`/s/${roomCode}`, { replace: true });
          return;
        }

        if (!response.ok || data.canEnter !== true) {
          clearStoredRoomSession(roomCode);
          setError(data.error || 'You need to reserve a place before joining this room.');
          return;
        }

        updateStoredRoomSessionFromAccess(roomCode, data);

        logVideoDiagnostic('info', 'room-access-ok', {
          source: 'room-load',
          isHost: data.isHost === true,
          isInvited: data.isInvited === true,
          isConfirmedModel: data.isConfirmedModel === true,
        }, getRoomSessionDiagnosticContext(roomCode));

        setToken(storedToken);
        setServerUrl(import.meta.env.VITE_LIVEKIT_URL || 'ws://localhost:7880');
        setConductAccepted(
          hasAcceptedConductAgreement(roomCode, sessionStorage.getItem(`participant-id-${roomCode}`))
        );
        fetchRoomData();
      } catch (err) {
        if (!cancelled) {
          setError('Failed to check room access. Please try joining again.');
        }
      }
    }

    loadRoomSession();

    return () => {
      cancelled = true;
    };
  }, [code, getToken, navigate]);

  const fetchRoomData = async () => {
    try {
      const roomRes = await fetch(`/api/rooms/${code}`);
      if (roomRes.ok) {
        const data = await roomRes.json();
        if (data.room?.closedAt) {
          navigate(`/s/${code}/summary`, { replace: true });
          return;
        }
        setRoom(data.room);
      }
    } catch (err) {
      console.error('Error fetching room data:', err);
    }
  };

  const handleStartSession = async () => {
    const confirmed = window.confirm(
      'Starting the session will allow guests with tickets or invitations to enter the room.\n\nAre you ready to start?'
    );
    if (!confirmed) return;

    try {
      const authToken = await getToken();
      const response = await fetch(`/api/rooms/${code}/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setRoom(data.room);
      } else {
        const data = await response.json().catch(() => ({}));
        window.alert(data.error || 'Failed to open the session.');
      }
    } catch (err) {
      console.error('Error starting session:', err);
      window.alert('Failed to open the session.');
    }
  };

  const handleEndSession = async () => {
    const confirmed = window.confirm(
      'End this session for everyone?\n\nPeople in the room will be taken to the end-of-session page. This cannot be undone.'
    );
    if (!confirmed) return;

    setIsEndingSession(true);
    try {
      const authToken = await getToken();
      const response = await fetch(`/api/rooms/${code}/close`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
      });

      if (response.ok) {
        // RoomProvider handles ended-state navigation; model recording uploads resume on the reference-pack page.
        return;
      }

      const data = await response.json().catch(() => ({}));
      window.alert(data.error || 'Failed to end the session.');
    } catch (err) {
      console.error('Error ending session:', err);
      window.alert('Failed to end the session.');
    } finally {
      setIsEndingSession(false);
    }
  };

  const retryMediaAccess = useCallback(() => {
    mediaFallbackStartedRef.current = false;
    setMediaAccessIssue(null);
    setConnectWithMedia(true);
    setLiveKitRoomKey((key) => key + 1);
  }, []);

  const continueWithoutMedia = useCallback(() => {
    setMediaAccessIssue(null);
    setConnectWithMedia(false);
    setLiveKitRoomKey((key) => key + 1);
  }, []);

  const handleLiveKitError = useCallback((err: Error) => {
    console.error('[RoomPage] LiveKit connection error:', err);
    logVideoDiagnostic('error', 'livekit-room-error', {
      error: err,
      connectWithMedia,
      mediaFallbackAlreadyStarted: mediaFallbackStartedRef.current,
    }, {
      roomCode: code ?? null,
      participantId: code ? sessionStorage.getItem(`participant-id-${code}`) : null,
      participantName: code ? sessionStorage.getItem(`participant-name-${code}`) : null,
      participantRole: code ? sessionStorage.getItem(`participant-role-${code}`) : null,
    });

    const mediaIssue = getMediaAccessIssue(err);
    if (mediaIssue) {
      if (!mediaFallbackStartedRef.current) {
        mediaFallbackStartedRef.current = true;
        setMediaAccessIssue(mediaIssue);
        setConnectWithMedia(false);
        setLiveKitRoomKey((key) => key + 1);
      }
      return;
    }

    if (err.message?.includes('invalid authorization token') || err.message?.includes('token')) {
      if (code) clearStoredRoomSession(code);
      setError('Your session token has expired. Please rejoin the room.');
    } else {
      setError(err.message);
    }
  }, [code, connectWithMedia]);

  const handleMediaDeviceFailure = useCallback((failure?: MediaDeviceFailure, kind?: MediaDeviceKind) => {
    handleLiveKitError(createMediaDeviceFailureError(failure, kind));
  }, [handleLiveKitError]);

  const handleAcceptConduct = useCallback(() => {
    if (!code) return;

    const participantId = sessionStorage.getItem(`participant-id-${code}`);
    markConductAgreementAccepted(code, participantId);
    setConductAccepted(true);
    logVideoDiagnostic('info', 'conduct-accepted', null, getRoomSessionDiagnosticContext(code));
  }, [code]);

  const initialCameraProfile: LiveKitCameraProfile = 'adaptive';

  const roomOptions: RoomOptions = useMemo(() => getLiveKitRoomOptions({
    profile: initialCameraProfile,
    tuning: DEFAULT_HI_RES_TUNING_SETTINGS,
    preferredCameraDeviceId,
    preferredMicrophoneDeviceId,
  }), [initialCameraProfile, preferredCameraDeviceId, preferredMicrophoneDeviceId]);
  const publishCameraOnJoin = connectWithMedia;
  const publishMicrophoneOnJoin = false;


  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-bg">
        <div className="bg-panel border border-line rounded-xl p-6 max-w-md w-full text-center shadow-md">
          <h2 className="font-display text-xl font-medium text-warn mb-2">Connection Error</h2>
          <p className="text-text-dim mb-4">{error}</p>
          <button onClick={() => navigate(code ? `/s/${code}` : '/')} className="btn">Back to Event</button>
        </div>
      </div>
    );
  }

  if (!token || !serverUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-text-dim">Connecting...</div>
      </div>
    );
  }

  if (code && shouldRequireConductAgreement(code, conductAccepted)) {
    return (
      <CodeOfConductGate
        onAgree={handleAcceptConduct}
        onBackToEvent={() => navigate(`/s/${code}`)}
      />
    );
  }

  return (
    <LiveKitRoom
      key={liveKitRoomKey}
      token={token}
      serverUrl={serverUrl}
      connect={true}
      audio={publishMicrophoneOnJoin}
      video={publishCameraOnJoin}
      onError={handleLiveKitError}
      onMediaDeviceFailure={handleMediaDeviceFailure}
      options={roomOptions}
    >
      <RoomProvider roomCode={code!}>
        <LiveKitDiagnostics
          roomOptions={roomOptions}
          connectWithMedia={connectWithMedia}
        />
        <LocalCameraPublishProfile />
        <RoomDiagnosticsOverlay />
        <BrowserSourceRecorder roomCode={code!} />
        {mediaAccessIssue && (
          <MediaAccessNotice
            issue={mediaAccessIssue}
            onRetry={retryMediaAccess}
            onContinue={continueWithoutMedia}
            onBackToEvent={() => navigate(code ? `/s/${code}` : '/')}
          />
        )}
        <SessionLayout
          code={code!}
          room={room}
          navigate={navigate}
          handleStartSession={handleStartSession}
          handleEndSession={handleEndSession}
          isEndingSession={isEndingSession}
          hiResTuningSettings={DEFAULT_HI_RES_TUNING_SETTINGS}
        />
        <SessionRoomAudioRenderer />
      </RoomProvider>
    </LiveKitRoom>
  );
}

function SessionRoomAudioRenderer() {
  const tracks = useTracks(
    [Track.Source.Microphone, Track.Source.Unknown],
    {
      updateOnlyOn: [],
      onlySubscribed: true,
    },
  ).filter((trackRef) => !trackRef.participant.isLocal && trackRef.publication.kind === Track.Kind.Audio);

  return (
    <div style={{ display: 'none' }}>
      {tracks.map((trackRef) => (
        <AudioTrack
          key={`${trackRef.participant.identity}-${trackRef.publication.trackSid ?? trackRef.publication.trackName}`}
          trackRef={trackRef}
        />
      ))}
    </div>
  );
}

function CodeOfConductGate({
  onAgree,
  onBackToEvent,
}: {
  onAgree: () => void;
  onBackToEvent: () => void;
}) {
  const conductItems = [
    'Treat the model, organizer, and other artists with respect.',
    'Not record, screenshot, photograph, stream, or redistribute the live video or audio.',
    'Only share your own artwork, not images or recordings of the model or session.',
  ];

  return (
    <div className="min-h-screen bg-bg px-4 py-8 flex items-center justify-center">
      <div className="card w-full max-w-xl">
        <div className="page-eyebrow">Live Session</div>
        <h1 className="type-title-modal text-text">Before you join</h1>
        <p className="type-body-sm mt-4 text-text-dim">
          This is a live figure drawing session with a real model. Help keep the room respectful, focused, and safe.
        </p>
        <p className="type-body-sm mt-6 text-text">You agree to:</p>

        <ul className="mt-3 space-y-3">
          {conductItems.map((item) => (
            <li key={item} className="flex gap-3 type-body-sm text-text">
              <span className="mt-[0.65em] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <button type="button" onClick={onAgree} className="btn btn-accent btn-lg min-h-[52px] flex-1">
            I Agree
          </button>
          <button type="button" onClick={onBackToEvent} className="btn btn-secondary btn-lg min-h-[52px] sm:flex-none">
            Back To Event
          </button>
        </div>
      </div>
    </div>
  );
}

function MediaAccessNotice({
  issue,
  onRetry,
  onContinue,
  onBackToEvent,
}: {
  issue: MediaAccessIssue;
  onRetry: () => void;
  onContinue: () => void;
  onBackToEvent: () => void;
}) {
  return (
    <div className="fixed inset-x-0 top-4 z-50 flex justify-center px-4 pointer-events-none">
      <div className="w-full max-w-2xl rounded-xl border border-warn/30 bg-panel shadow-xl p-4 pointer-events-auto">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 h-9 w-9 rounded-full bg-warn/10 flex items-center justify-center text-warn flex-shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-[19px] text-text leading-tight">
              {issue.title}
            </h2>
            <p className="text-[14px] text-text-dim mt-1 leading-relaxed">
              {issue.message}
            </p>
            <p className="text-[12.5px] text-text-faint mt-1.5 leading-relaxed">
              {issue.detail}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={onRetry} className="btn btn-accent">
                Try camera/mic again
              </button>
              <button onClick={onContinue} className="btn">
                Enter without camera/mic
              </button>
              <button onClick={onBackToEvent} className="btn btn-secondary">
                Back to Event
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface SessionLayoutProps {
  code: string;
  room: Room | null;
  navigate: ReturnType<typeof useNavigate>;
  handleStartSession: () => void;
  handleEndSession: () => void;
  isEndingSession: boolean;
  hiResTuningSettings: HiResTuningSettings;
}

function SessionClosedNotice({ className }: { className: string }) {
  return (
    <div className={className}>
      <div className="rounded-xl border border-white/20 bg-black/78 px-5 py-3.5 text-center text-white shadow-2xl backdrop-blur-md">
        <div className="text-lg font-semibold leading-tight">Session Closed.</div>
        <div className="mt-1 text-sm leading-snug text-white/82">
          Open the session to allow ticket holders in
        </div>
      </div>
    </div>
  );
}

function SessionLayout({
  code,
  room,
  navigate,
  handleStartSession,
  handleEndSession,
  isEndingSession,
  hiResTuningSettings,
}: SessionLayoutProps) {
  const { getToken } = useAuth();
  // Get state from context - single source of truth that updates reactively
  const {
    isHost,
    modelParticipantId,
    modelProfileId,
    modelParticipantIds,
    modelCameraLabels,
    pinnedArtworkId,
    galleryArtworkFavorites,
    stagePinnedParticipantId,
    stageDisplayMode,
    recording,
    schedule,
    updateSchedule,
    participants: sessionParticipants,
    browserAudio,
    startedAt,
    sessionName,
    pinArtwork,
    clearPinnedArtwork,
    clearStagePinnedParticipant,
  } = useRoom();
  const { localParticipant } = useLocalParticipant();
  const participants = useParticipants();

  // Get model participant info for artwork attribution
  const primaryModelParticipantId = modelParticipantId ?? modelParticipantIds[0];
  const modelParticipant = sessionParticipants.find(p => p.participantId === primaryModelParticipantId);
  const modelDisplayName = modelParticipant?.displayName || '';
  const modelProfileIdForArtwork = modelProfileId || modelParticipant?.profileId || undefined;

  // Get current user's profile ID from sessionStorage
  const [artistProfileId] = useState(() => sessionStorage.getItem(`participant-profileId-${code}`) || undefined);
  const localParticipantProfileId = useMemo(() => (
    localParticipant?.identity
      ? sessionParticipants.find(participant => participant.participantId === localParticipant.identity)?.profileId ??
        sessionStorage.getItem(`participant-profileId-${code}`)
      : null
  ), [code, localParticipant?.identity, sessionParticipants]);

  // Dynamically compute if current user is model
  // This reacts to changes when someone is designated as model during the session
  const isCurrentUserModel = isActiveModelCameraParticipant({
    participantId: localParticipant?.identity,
    participantProfileId: localParticipantProfileId,
    modelParticipantId,
    modelProfileId,
    modelParticipantIds,
  });
  const isHostOrModel = isHost || isCurrentUserModel;
  const modelParticipantCandidateIds = modelParticipantIds;
  const modelCameraParticipants = useMemo(() => {
    const participantsByIdentity = new Map(participants.map(participant => [participant.identity, participant]));
    const orderedParticipants: LiveKitParticipant[] = [];

    for (const identity of modelParticipantCandidateIds) {
      const participant = participantsByIdentity.get(identity);
      if (participant && hasActiveCameraPublication(participant)) {
        orderedParticipants.push(participant);
      }
    }

    return orderedParticipants.sort((a, b) =>
      getCameraLabelSortValue(modelCameraLabels[a.identity]) - getCameraLabelSortValue(modelCameraLabels[b.identity])
    );
  }, [modelCameraLabels, modelParticipantCandidateIds, participants]);
  const modelCameraIds = useMemo(
    () => modelCameraParticipants.map(participant => participant.identity),
    [modelCameraParticipants]
  );

  // Use centralized pose timer hook
  const { poseState, displayPoseState, remaining, elapsed } = usePoseTimer();
  usePoseSoundEffects({ poseState: displayPoseState, remaining });

  // UI state
  const [showNewPoseModal, setShowNewPoseModal] = useState(false);
  const [showDeviceSettings, setShowDeviceSettings] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [localPinnedParticipantId, setLocalPinnedParticipantId] = useState<string | null>(null);
  const [selectedModelCameraId, setSelectedModelCameraId] = useState<string | null>(null);
  const pinnedModelParticipantId = localPinnedParticipantId && modelParticipantCandidateIds.includes(localPinnedParticipantId)
    ? localPinnedParticipantId
    : null;
  const localModelCameraId = isCurrentUserModel && localParticipant?.identity && modelCameraIds.includes(localParticipant.identity)
    ? localParticipant.identity
    : null;
  const preferredModelCameraId = localModelCameraId ?? modelCameraIds[0] ?? null;
  const activeModelCameraId = selectedModelCameraId && modelCameraIds.includes(selectedModelCameraId)
    ? selectedModelCameraId
    : preferredModelCameraId;
  const pinnedModelCameraId = pinnedModelParticipantId && modelCameraIds.includes(pinnedModelParticipantId)
    ? pinnedModelParticipantId
    : null;
  const [pinnedArtwork, setPinnedArtwork] = useState<SessionArtwork | null>(null);
  const [participantId] = useState(() => sessionStorage.getItem(`participant-id-${code}`) || '');
  const [participantName] = useState(() => sessionStorage.getItem(`participant-name-${code}`) || 'Artist');
  const [schedulePanelHeight, setSchedulePanelHeight] = useState(200);
  const [artistFocusMode, setArtistFocusMode] = useState(false);
  const [areFullscreenPoseControlsVisible, setAreFullscreenPoseControlsVisible] = useState(true);
  const isResizingRef = useRef(false);
  const activeResizePointerIdRef = useRef<number | null>(null);
  const resizeBodyStyleRef = useRef<{ cursor: string; userSelect: string; touchAction: string } | null>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const previousViewerFocusModeRef = useRef(false);
  const fullscreenPoseControlsIdleTimerRef = useRef<number | null>(null);
  const displayedPinnedArtwork = pinnedArtwork
    ? applyArtworkFavoriteState(pinnedArtwork, galleryArtworkFavorites, participantId)
    : null;

  // Use centralized pose controls hook
  const {
    handlePlay,
    handlePause,
    handleRestart,
    handleJumpTo,
  } = usePoseControls();

  useEffect(() => {
    if (modelCameraIds.length === 0) {
      if (selectedModelCameraId !== null) {
        setSelectedModelCameraId(null);
      }
      if (pinnedModelParticipantId) {
        setLocalPinnedParticipantId(null);
      }
      return;
    }

    if (selectedModelCameraId && !modelCameraIds.includes(selectedModelCameraId)) {
      setSelectedModelCameraId(null);
    }

    if (pinnedModelParticipantId && !modelCameraIds.includes(pinnedModelParticipantId)) {
      setLocalPinnedParticipantId(null);
    }
  }, [modelCameraIds, pinnedModelParticipantId, selectedModelCameraId]);

  useEffect(() => {
    if (!pinnedArtworkId) {
      setPinnedArtwork(null);
      return;
    }

    let cancelled = false;

    async function loadPinnedArtwork() {
      try {
        const response = await fetch(`/api/rooms/${code}/artwork`, {
          headers: {
            'x-participant-id': participantId,
          },
        });
        if (!response.ok) {
          throw new Error('Failed to load artwork spotlight');
        }

        const data = await response.json();
        const nextArtwork = (data.artwork as SessionArtwork[] | undefined)?.find((item) => item.id === pinnedArtworkId) ?? null;
        if (!cancelled) {
          setPinnedArtwork(nextArtwork);
        }
      } catch (error) {
        console.error('Error loading artwork spotlight:', error);
        if (!cancelled) {
          setPinnedArtwork(null);
        }
      }
    }

    void loadPinnedArtwork();

    return () => {
      cancelled = true;
    };
  }, [code, participantId, pinnedArtworkId]);

  const handlePinArtwork = useCallback((artwork: SessionArtwork) => {
    if (!isHost) return;
    setPinnedArtwork(artwork);
    pinArtwork(artwork.id);
  }, [isHost, pinArtwork]);

  // Favorite state is broadcast in unified session state and derived above.
  const handleArtworkLike = (_artworkId: string) => undefined;

  // Placeholder for gallery count (no longer needed for ViewToggle)
  useEffect(() => {
    const fetchGalleryCount = async () => {
      try {
        const response = await fetch(`/api/rooms/${code}/artwork/count`);
        if (response.ok) {
          // Gallery count no longer shown in header
        }
      } catch (error) {
        console.error('Error fetching gallery count:', error);
      }
    };

    fetchGalleryCount();
    const interval = setInterval(fetchGalleryCount, 30000);
    return () => clearInterval(interval);
  }, [code]);

  // Save schedule to server (synced to all participants via unified state)
  const handleSaveSchedule = useCallback((newSchedule: ScheduleItem[]) => {
    updateSchedule(newSchedule);
  }, [updateSchedule]);

  // Resize handlers for schedule panel
  const stopScheduleResize = useCallback(() => {
    if (!isResizingRef.current) return;

    isResizingRef.current = false;
    activeResizePointerIdRef.current = null;

    const previousBodyStyle = resizeBodyStyleRef.current;
    if (previousBodyStyle) {
      document.body.style.cursor = previousBodyStyle.cursor;
      document.body.style.userSelect = previousBodyStyle.userSelect;
      document.body.style.touchAction = previousBodyStyle.touchAction;
      resizeBodyStyleRef.current = null;
    }
  }, []);

  const handleResizeStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    isResizingRef.current = true;
    activeResizePointerIdRef.current = e.pointerId;
    resizeBodyStyleRef.current = {
      cursor: document.body.style.cursor,
      userSelect: document.body.style.userSelect,
      touchAction: document.body.style.touchAction,
    };
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    document.body.style.touchAction = 'none';
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, []);

  useEffect(() => {
    const handlePointerMove = (e: PointerEvent) => {
      if (!isResizingRef.current || !sidebarRef.current) return;
      if (activeResizePointerIdRef.current !== null && e.pointerId !== activeResizePointerIdRef.current) return;

      e.preventDefault();

      const sidebarRect = sidebarRef.current.getBoundingClientRect();
      const newHeight = e.clientY - sidebarRect.top;

      // Clamp between 100px and sidebar height minus 100px (for chat minimum)
      const minHeight = 100;
      const maxHeight = sidebarRect.height - 100;
      setSchedulePanelHeight(Math.max(minHeight, Math.min(maxHeight, newHeight)));
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', stopScheduleResize);
    document.addEventListener('pointercancel', stopScheduleResize);
    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', stopScheduleResize);
      document.removeEventListener('pointercancel', stopScheduleResize);
      stopScheduleResize();
    };
  }, [stopScheduleResize]);

  const handleUpload = async (formData: FormData) => {
    if (!code) throw new Error('Room code is unavailable');

    const authToken = await getToken();
    await uploadRoomArtwork(code, formData, authToken);
  };

  // Determine role for header display
  const headerRole = isHost ? 'host' : isHostOrModel ? 'model' : 'artist';
  const isActivePose =
    poseState.isActive &&
    !poseState.isPaused &&
    !poseState.isBreak &&
    !poseState.isUntimedBreak;
  const roomControlActivity: 'idle' | 'pose' = isActivePose ? 'pose' : 'idle';
  const isPoseFocusEligible =
    poseState.isActive &&
    !poseState.isBreak &&
    !poseState.isUntimedBreak;
  const shouldShowModelCameraSwitcher = isCurrentUserModel || isActivePose;
  const isActiveBreak =
    poseState.isActive &&
    (poseState.isBreak || poseState.isUntimedBreak);
  const shouldFillVideoStage = isActivePose || isActiveBreak;
  const isArtistViewer = !isHostOrModel;
  const activePoseFocusKey = isPoseFocusEligible
    ? [
      poseState.startedAt ?? 'session',
      poseState.currentPose,
      poseState.poseName,
    ].join(':')
    : null;
  const shouldUseViewerFocusMode = isArtistViewer && isPoseFocusEligible && artistFocusMode;
  const shouldAutoHideFullscreenPoseControls = shouldUseViewerFocusMode && isActivePose;
  const fullscreenPoseControlsVisible =
    !shouldAutoHideFullscreenPoseControls || areFullscreenPoseControlsVisible;
  const fullscreenPoseControlsClassName = fullscreenPoseControlsVisible
    ? 'opacity-100'
    : 'pointer-events-none opacity-0';
  const showSessionClosedNotice = !startedAt;
  const browserAudioTitle = browserAudio?.sourceTitle?.trim() || null;
  const renderPoseCountdownStatus = () => roomControlActivity === 'pose' ? (
    <PoseFocusTimer
      poseState={poseState}
      remaining={remaining}
      elapsed={elapsed}
      largerOnMobile={isCurrentUserModel}
      className=""
    />
  ) : null;
  const renderPoseCountdownOverlay = () => {
    const status = renderPoseCountdownStatus();
    if (!status) return null;

    return (
      <div className="absolute left-3 top-3 z-30 flex max-w-[calc(100%-5.5rem)] items-start sm:left-4 sm:top-4">
        {status}
      </div>
    );
  };
  const handleNextPose = useCallback(() => {
    const nextIndex = schedule.findIndex((_, index) => index > poseState.currentPose);
    if (nextIndex >= 0) {
      handleJumpTo(nextIndex);
    }
  }, [handleJumpTo, poseState.currentPose, schedule]);
  const poseTransportControls = isHostOrModel ? (
    <PoseTransportControls
      poseState={poseState}
      schedule={schedule}
      onPlay={handlePlay}
      onPause={handlePause}
      onRestart={handleRestart}
      onNext={handleNextPose}
    />
  ) : null;
  const selectedModelCameraSwitcherId = isCurrentUserModel && !isActivePose
    ? pinnedModelCameraId
    : activeModelCameraId;
  const activePinnedParticipantId = localPinnedParticipantId ?? stagePinnedParticipantId;
  const canClearActiveParticipantPin = !!localPinnedParticipantId || (isHost && !!stagePinnedParticipantId);
  const activeParticipantPinActionLabel = localPinnedParticipantId ? 'Unpin' : 'Clear spotlight';
  const clearActiveParticipantPin = useCallback(() => {
    if (localPinnedParticipantId) {
      setLocalPinnedParticipantId(null);
      return;
    }
    if (isHost && stagePinnedParticipantId) {
      clearStagePinnedParticipant();
    }
  }, [clearStagePinnedParticipant, isHost, localPinnedParticipantId, stagePinnedParticipantId]);
  const displayedArtworkSpotlight = !localPinnedParticipantId ? displayedPinnedArtwork : null;
  const shouldShowPinnedArtworkLoading = !localPinnedParticipantId && !displayedArtworkSpotlight && !!pinnedArtworkId;
  const shouldUseParticipantGrid =
    stageDisplayMode === 'grid' &&
    !isActivePose &&
    !activePinnedParticipantId &&
    !displayedArtworkSpotlight &&
    !shouldShowPinnedArtworkLoading;
  const roomShellClassName = 'min-h-[100svh] lg:h-screen flex flex-col bg-bg overflow-x-hidden lg:overflow-hidden';
  const mainContentClassName = isCurrentUserModel
    ? 'flex-1 min-h-0 overflow-visible flex flex-col lg:overflow-hidden lg:grid lg:grid-cols-[1fr_340px]'
    : 'flex-1 min-h-0 overflow-y-auto lg:overflow-hidden flex flex-col lg:grid lg:grid-cols-[1fr_340px]';
  const stagePaneClassName = [
    'flex flex-col bg-bg lg:h-full',
    shouldFillVideoStage
      ? 'flex-1 min-h-0'
      : isCurrentUserModel
        ? 'min-h-0 flex-shrink-0'
        : 'min-h-[360px] flex-shrink-0 lg:min-h-0',
  ].join(' ');
  const videoPaneClassName = [
    'relative overflow-hidden bg-[#0a0807] lg:h-auto lg:flex-1 lg:min-h-0',
    shouldFillVideoStage
      ? 'flex-1 min-h-[280px]'
      : isCurrentUserModel
        ? 'h-[56svh] min-h-[360px] max-h-[620px] flex-shrink-0 lg:max-h-none'
        : 'h-[52svh] min-h-[280px]',
  ].join(' ');
  const participantStripClassName = 'flex items-stretch h-[76px] lg:h-[84px] flex-shrink-0';
  const sidebarClassName = 'hidden lg:flex h-full max-h-full min-h-0 overflow-y-auto overscroll-contain border-l border-line flex-col bg-panel';
  const sidebarScheduleHeightStyle = { height: schedulePanelHeight, flexShrink: 0 };
  const mobileTabsClassName = 'min-h-0';
  const baseMobileTabs = ['schedule', 'participants', 'chat', 'gallery'];
  const mobileTabs = isHostOrModel ? [...baseMobileTabs, 'controls'] : baseMobileTabs;
  const renderPoseSchedulePanel = (
    variant: 'default' | 'modelMobile' = 'default',
    displayMode: 'full' | 'timerOnly' | 'scheduleOnly' = 'full',
  ) => (
    <PoseSchedulePanel
      poseState={{ ...poseState, remaining, elapsed }}
      schedule={schedule}
      variant={variant}
      displayMode={displayMode}
      isEditable={isHostOrModel}
      onRestart={isHostOrModel ? handleRestart : undefined}
      onNewPose={isHostOrModel ? () => setShowNewPoseModal(true) : undefined}
      onPlay={isHostOrModel ? handlePlay : undefined}
      onPause={isHostOrModel ? handlePause : undefined}
      onJumpTo={isHostOrModel ? handleJumpTo : undefined}
      checksRecordingStorage={isCurrentUserModel}
    />
  );
  const renderSessionControls = () => (
    <>
      <HostControlsPanel
        onOpenSession={handleStartSession}
        onEndSession={handleEndSession}
        hasSessionStarted={!!startedAt}
        isEndingSession={isEndingSession}
        variant="mobileTab"
      />
    </>
  );
  const renderGridMediaControls = () => {
    const localParticipantId = localParticipant?.identity ?? null;
    const isLocalCameraPinned = !!localParticipantId && localPinnedParticipantId === localParticipantId;

    return (
      <MediaControls
        className="absolute bottom-3 left-1/2 z-30 -translate-x-1/2"
        canPinLocalCamera={isCurrentUserModel && roomControlActivity === 'idle'}
        isLocalCameraPinned={isLocalCameraPinned}
        onToggleLocalCameraPin={
          localParticipantId
            ? () => setLocalPinnedParticipantId(isLocalCameraPinned ? null : localParticipantId)
            : undefined
        }
      />
    );
  };

  useEffect(() => {
    if (!isArtistViewer || !isPoseFocusEligible || !activePoseFocusKey) {
      setArtistFocusMode(false);
      return;
    }

    setArtistFocusMode(true);
  }, [activePoseFocusKey, isArtistViewer, isPoseFocusEligible]);

  useEffect(() => {
    const wasUsingViewerFocusMode = previousViewerFocusModeRef.current;
    previousViewerFocusModeRef.current = shouldUseViewerFocusMode;

    if (wasUsingViewerFocusMode && !shouldUseViewerFocusMode) {
      void exitRoomDocumentFullscreen();
    }
  }, [shouldUseViewerFocusMode]);

  useEffect(() => {
    if (fullscreenPoseControlsIdleTimerRef.current !== null) {
      window.clearTimeout(fullscreenPoseControlsIdleTimerRef.current);
      fullscreenPoseControlsIdleTimerRef.current = null;
    }

    if (!shouldAutoHideFullscreenPoseControls) {
      setAreFullscreenPoseControlsVisible(true);
      return;
    }

    const scheduleHide = () => {
      if (fullscreenPoseControlsIdleTimerRef.current !== null) {
        window.clearTimeout(fullscreenPoseControlsIdleTimerRef.current);
      }
      fullscreenPoseControlsIdleTimerRef.current = window.setTimeout(() => {
        setAreFullscreenPoseControlsVisible(false);
        fullscreenPoseControlsIdleTimerRef.current = null;
      }, FULLSCREEN_POSE_CONTROLS_IDLE_MS);
    };

    const showControls = () => {
      setAreFullscreenPoseControlsVisible(true);
      scheduleHide();
    };

    showControls();
    window.addEventListener('pointermove', showControls, { passive: true });
    window.addEventListener('pointerdown', showControls, { passive: true });
    window.addEventListener('keydown', showControls);

    return () => {
      window.removeEventListener('pointermove', showControls);
      window.removeEventListener('pointerdown', showControls);
      window.removeEventListener('keydown', showControls);
      if (fullscreenPoseControlsIdleTimerRef.current !== null) {
        window.clearTimeout(fullscreenPoseControlsIdleTimerRef.current);
        fullscreenPoseControlsIdleTimerRef.current = null;
      }
    };
  }, [shouldAutoHideFullscreenPoseControls]);

  useEffect(() => () => {
    if (previousViewerFocusModeRef.current) {
      void exitRoomDocumentFullscreen();
    }
  }, []);

  const handleModelCameraSelect = useCallback((identity: string) => {
    if (isCurrentUserModel) {
      const shouldUnpin = pinnedModelCameraId === identity;
      setLocalPinnedParticipantId(shouldUnpin ? null : identity);
      if (!shouldUnpin) {
        setSelectedModelCameraId(identity);
      }
      return;
    }

    setSelectedModelCameraId(identity);
    setLocalPinnedParticipantId(null);
  }, [isCurrentUserModel, pinnedModelCameraId]);

  if (shouldUseViewerFocusMode) {
    return (
      <div className={`h-[100svh] bg-black relative overflow-hidden ${fullscreenPoseControlsVisible ? '' : 'cursor-none'}`}>
        <ModelFrameStreamer tuning={hiResTuningSettings} />
        <FeaturedVideo
          localPinnedParticipantId={localPinnedParticipantId}
          onLocalCameraPinChange={setLocalPinnedParticipantId}
          stagePinnedParticipantId={stagePinnedParticipantId}
          selectedModelCameraParticipantId={activeModelCameraId}
          isPinned={canClearActiveParticipantPin && !!activePinnedParticipantId}
          onUnpin={clearActiveParticipantPin}
          unpinLabel={activeParticipantPinActionLabel}
          useActiveSpeakerFallback={false}
          fullScreen
          canUseArtistTools={!isCurrentUserModel}
          controlsVisible={fullscreenPoseControlsVisible}
          showPinSelfControl={isCurrentUserModel && roomControlActivity === 'idle'}
          topLeftStatus={renderPoseCountdownStatus()}
          poseTransportControls={poseTransportControls}
        />
        {showSessionClosedNotice && (
          <SessionClosedNotice className="pointer-events-none absolute bottom-[8.75rem] left-1/2 z-10 w-[calc(100%-1.5rem)] max-w-xl -translate-x-1/2 sm:bottom-[5.25rem]" />
        )}
        {browserAudioTitle && (
          <div
            className="pointer-events-none absolute left-4 top-[3.75rem] z-30 max-w-[70vw] truncate rounded-md bg-black/35 px-2 py-1 text-[11px] font-medium leading-tight text-white/60 shadow-sm backdrop-blur-sm sm:max-w-md"
            title={browserAudioTitle}
          >
            {browserAudioTitle}
          </div>
        )}
        {shouldShowModelCameraSwitcher && (
          <ModelCameraSwitcher
            cameras={modelCameraParticipants}
            cameraLabels={modelCameraLabels}
            selectedCameraId={selectedModelCameraSwitcherId}
            onSelect={handleModelCameraSelect}
            className={`absolute bottom-[4.5rem] left-3 z-20 transition-opacity duration-300 ease-out sm:bottom-5 sm:left-5 ${fullscreenPoseControlsClassName}`}
          />
        )}
      </div>
    );
  }

  return (
    <div className={roomShellClassName}>

      {/* Audio debug panel - only shown when ?debug=true */}
      {new URLSearchParams(window.location.search).get('debug') === 'true' && (
        <AudioDebugPanel />
      )}

      {/* HEADER */}
      <SessionHeader
        roomCode={code}
        roomName={sessionName ?? room?.name}
        role={headerRole}
        onSettings={isHost ? () => {
          window.open(`/event/${code}`, '_blank', 'noopener,noreferrer');
        } : undefined}
      />

      {/* MAIN CONTENT */}
      <div className={mainContentClassName}>
        {/* STAGE PANE */}
        <div className={stagePaneClassName}>
          {/* Video Feed */}
          <div className={videoPaneClassName}>
            {/* Model-only still capture and LiveKit quality hints */}
            <ModelFrameStreamer tuning={hiResTuningSettings} />

            {displayedArtworkSpotlight ? (
              <PinnedArtworkOverlay
                artwork={displayedArtworkSpotlight}
                canClose={isHost}
                onClose={clearPinnedArtwork}
                onLike={handleArtworkLike}
                participantId={participantId}
                roomCode={code}
              />
            ) : shouldShowPinnedArtworkLoading ? (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-[#0a0807] text-sm text-white/70">
                Loading artwork spotlight...
              </div>
            ) : shouldUseParticipantGrid ? (
              <>
                <StageParticipantGrid />
                {renderGridMediaControls()}
              </>
            ) : (
              <FeaturedVideo
                localPinnedParticipantId={localPinnedParticipantId}
                onLocalCameraPinChange={setLocalPinnedParticipantId}
                stagePinnedParticipantId={stagePinnedParticipantId}
                selectedModelCameraParticipantId={activeModelCameraId}
                isPinned={canClearActiveParticipantPin && !!activePinnedParticipantId}
                onUnpin={clearActiveParticipantPin}
                unpinLabel={activeParticipantPinActionLabel}
                useActiveSpeakerFallback={stageDisplayMode === 'active-speaker'}
                canUseArtistTools={!isCurrentUserModel}
                showPinSelfControl={isCurrentUserModel && roomControlActivity === 'idle'}
                topLeftStatus={renderPoseCountdownStatus()}
                poseTransportControls={poseTransportControls}
              />
            )}
            {(displayedArtworkSpotlight || shouldShowPinnedArtworkLoading) && renderPoseCountdownOverlay()}
            {showSessionClosedNotice && (
              <SessionClosedNotice className="pointer-events-none absolute bottom-[8.75rem] left-1/2 z-10 w-[calc(100%-1.5rem)] max-w-xl -translate-x-1/2 sm:bottom-[5.25rem]" />
            )}
            {shouldShowModelCameraSwitcher && !pinnedArtworkId && !pinnedArtwork && (
              <ModelCameraSwitcher
                cameras={modelCameraParticipants}
                cameraLabels={modelCameraLabels}
                selectedCameraId={selectedModelCameraSwitcherId}
                onSelect={handleModelCameraSelect}
                className="absolute left-3 bottom-[4.5rem] z-20"
              />
            )}

          </div>

          {/* Participant Strip */}
          <div className={participantStripClassName}>
            <ParticipantStrip onSelectFeatured={setLocalPinnedParticipantId} pinnedParticipant={localPinnedParticipantId} />
          </div>
        </div>

        <div className="lg:hidden flex-shrink-0 border-t border-line bg-panel">
          {roomControlActivity === 'idle' && (
            <div className="border-b border-line-soft">
              {renderPoseSchedulePanel('modelMobile', 'timerOnly')}
            </div>
          )}
          <SidebarPanel
            roomCode={code}
            participantId={participantId}
            artistProfileId={artistProfileId}
            artistName={participantName}
            onUpload={() => setShowUploadModal(true)}
            onPinArtwork={handlePinArtwork}
            canPinArtwork={isHost}
            onSelectParticipant={setLocalPinnedParticipantId}
            pinnedParticipant={localPinnedParticipantId}
            scheduleContent={renderPoseSchedulePanel('modelMobile', 'scheduleOnly')}
            controlsContent={isHostOrModel ? renderSessionControls() : undefined}
            initialTab="schedule"
            tabs={mobileTabs as SidebarTab[]}
            className={mobileTabsClassName}
            dockChatInputOnMobile
          />
        </div>

        {/* SIDEBAR */}
        <aside ref={sidebarRef} className={sidebarClassName}>
            {/* Pose Schedule Panel - resizable */}
            <div style={sidebarScheduleHeightStyle} className="flex flex-col min-h-0">
              {renderPoseSchedulePanel()}
            </div>

            {/* Resize handle */}
            <div
              role="separator"
              aria-orientation="horizontal"
              aria-label="Resize pose schedule"
              onPointerDown={handleResizeStart}
              className="hidden lg:flex h-4 flex-shrink-0 cursor-row-resize touch-none select-none hover:bg-accent/20 active:bg-accent/30 transition-colors items-center justify-center group"
            >
              <div className="w-8 h-0.5 rounded-full bg-line group-hover:bg-accent/50 transition-colors" />
            </div>

            {/* Chat & Gallery Tabs */}
            <div className="flex-1 min-h-[220px] flex flex-col">
              <SidebarPanel
                roomCode={code}
                participantId={participantId}
                artistProfileId={artistProfileId}
                artistName={participantName}
                onUpload={() => setShowUploadModal(true)}
                onPinArtwork={handlePinArtwork}
                canPinArtwork={isHost}
              />
            </div>

            {/* Host Controls - model/host selection */}
            <HostControlsPanel
              onOpenSession={handleStartSession}
              onEndSession={handleEndSession}
              hasSessionStarted={!!startedAt}
              isEndingSession={isEndingSession}
            />

          </aside>
        </div>

        {/* MODALS */}
      <NewPoseModal
        isOpen={showNewPoseModal}
        onClose={() => setShowNewPoseModal(false)}
        onSave={handleSaveSchedule}
        initialSchedule={schedule}
      />

      <DeviceSelector
        isOpen={showDeviceSettings}
        onClose={() => setShowDeviceSettings(false)}
      />

      <UploadArtworkModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleUpload}
        roomCode={code}
        participantId={participantId}
        participantName={participantName}
        artistProfileId={artistProfileId}
        modelName={modelDisplayName}
        modelProfileId={modelProfileIdForArtwork}
      />
    </div>
  );
}

function ModelCameraSwitcher({
  cameras,
  cameraLabels,
  selectedCameraId,
  onSelect,
  className = '',
}: {
  cameras: LiveKitParticipant[];
  cameraLabels: Record<string, string>;
  selectedCameraId: string | null;
  onSelect: (identity: string) => void;
  className?: string;
}) {
  if (cameras.length <= 1) return null;

  return (
    <div className={`${className} max-w-[calc(100%-1.5rem)]`}>
      <div className="flex max-w-full items-center gap-1 overflow-x-auto rounded border border-white/12 bg-[rgba(10,6,5,0.68)] p-1 shadow-lg backdrop-blur-md sm:gap-1.5 sm:p-1.5">
        <span className="hidden flex-shrink-0 px-1.5 text-[11px] font-medium uppercase text-white/65 sm:inline-flex">
          Camera
        </span>
        {cameras.map((camera, index) => {
          const isSelected = camera.identity === selectedCameraId;
          const displayName = camera.isLocal ? 'You' : (camera.name || `Camera ${index + 1}`);
          const cameraLabel = cameraLabels[camera.identity];
          if (!cameraLabel) return null;

          return (
            <button
              key={camera.identity}
              type="button"
              onClick={() => onSelect(camera.identity)}
              className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded text-[12px] font-semibold text-white transition-colors ${
                isSelected
                  ? 'bg-tag shadow-sm'
                  : 'bg-white/8 hover:bg-white/14'
              }`}
              title={`Switch to Camera ${cameraLabel}${displayName ? ` (${displayName})` : ''}`}
            >
              {cameraLabel}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function getCameraLabelSortValue(label: string | undefined) {
  if (!label) return Number.MAX_SAFE_INTEGER;
  const cleaned = label.trim().toUpperCase();
  if (!/^[A-Z]+$/.test(cleaned)) return Number.MAX_SAFE_INTEGER;

  let value = 0;
  for (const char of cleaned) {
    value = value * 26 + (char.charCodeAt(0) - 64);
  }
  return value - 1;
}

function PoseTransportControls({
  poseState,
  schedule,
  onPlay,
  onPause,
  onRestart,
  onNext,
}: {
  poseState: PoseTimerState;
  schedule: ScheduleItem[];
  onPlay: () => void;
  onPause: () => void;
  onRestart: () => void;
  onNext: () => void;
}) {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const firstMobileControlRef = useRef<HTMLButtonElement>(null);
  const isActive = poseState.isActive;
  const isUntimedBreak = poseState.isUntimedBreak;
  const isPaused = poseState.isPaused || isUntimedBreak;
  const canPlay = !isActive || isPaused;
  const canPause = isActive && !isPaused;
  const hasSchedule = schedule.length > 0;
  const hasNext = hasSchedule && poseState.currentPose < schedule.length - 1;
  const primaryLabel = canPause
    ? 'Pause pose'
    : isActive
      ? isUntimedBreak
        ? 'End break'
        : 'Resume pose'
      : hasSchedule
        ? 'Start pose'
        : 'Start untimed pose';
  const primaryAction = canPause ? onPause : onPlay;
  const buttonClassName = 'flex h-11 w-11 items-center justify-center rounded border border-white/12 bg-[rgba(10,6,5,0.62)] text-white backdrop-blur-md transition-colors hover:bg-black/70 disabled:cursor-not-allowed disabled:text-white/35 disabled:hover:bg-[rgba(10,6,5,0.62)] sm:h-9 sm:w-9';

  useEffect(() => {
    if (!isMobileOpen) return;

    firstMobileControlRef.current?.focus();

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (!containerRef.current?.contains(target)) {
        setIsMobileOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileOpen(false);
        launcherRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileOpen]);

  const renderControls = (firstButtonRef?: Ref<HTMLButtonElement>) => (
    <>
      <button
        ref={firstButtonRef}
        type="button"
        onClick={primaryAction}
        className={buttonClassName}
        aria-label={primaryLabel}
        title={primaryLabel}
      >
        {canPause ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>
      <button
        type="button"
        onClick={onRestart}
        disabled={!hasSchedule}
        className={buttonClassName}
        aria-label="Restart pose schedule"
        title="Restart pose schedule"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 12a9 9 0 1 0 3-6.7" />
          <path d="M3 4v5h5" />
        </svg>
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={!hasNext}
        className={buttonClassName}
        aria-label="Next pose"
        title="Next pose"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M5 5v14l8-7-8-7zm10 0h4v14h-4V5z" />
        </svg>
      </button>
    </>
  );

  return (
    <div ref={containerRef} className="relative">
      <div className="hidden items-center gap-2 sm:flex">
        {renderControls()}
      </div>
      <div className="sm:hidden">
        {isMobileOpen && (
          <div className="absolute bottom-full left-0 mb-2 flex flex-col items-start gap-2">
            {renderControls(firstMobileControlRef)}
          </div>
        )}
        <button
          ref={launcherRef}
          type="button"
          onClick={() => setIsMobileOpen(open => !open)}
          className={buttonClassName}
          aria-label="Pose controls"
          aria-expanded={isMobileOpen}
          aria-haspopup="true"
          title="Pose controls"
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 3v18" />
            <path d="M3 8h18" />
            <path d="M3 16h18" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function PoseFocusTimer({
  poseState,
  remaining,
  elapsed,
  compact = false,
  largerOnMobile = false,
  className = 'absolute left-3 top-3 z-20 sm:left-4 sm:top-4',
}: {
  poseState: PoseTimerState;
  remaining: number;
  elapsed: number;
  compact?: boolean;
  largerOnMobile?: boolean;
  className?: string;
}) {
  const displayTime = poseState.isUntimed ? elapsed : remaining;
  const minutes = Math.floor(Math.max(0, displayTime) / 60);
  const seconds = Math.max(0, displayTime) % 60;
  const label = poseState.isUntimed ? 'elapsed' : 'remaining';
  const containerSizeClassName = compact
    ? 'min-w-[84px] max-w-[34vw] px-2 py-1.5'
    : largerOnMobile
      ? 'min-w-[116px] max-w-[52vw] px-3 py-2 sm:min-w-[148px] sm:max-w-none sm:px-4 sm:py-3'
      : 'min-w-[84px] max-w-[34vw] px-2 py-1.5 sm:min-w-[148px] sm:max-w-none sm:px-4 sm:py-3';
  const labelClassName = compact
    ? 'text-[8px]'
    : largerOnMobile
      ? 'text-[9px] sm:text-[9px]'
      : 'text-[8px] sm:text-[9px]';
  const timeClassName = compact
    ? 'text-[21px]'
    : largerOnMobile
      ? 'text-[32px] sm:text-[34px]'
      : 'text-[21px] sm:text-[34px]';
  const poseNameClassName = compact
    ? 'hidden'
    : 'hidden sm:block mt-1 text-[12px] text-white/72 truncate';

  return (
    <div data-testid="active-pose-timer" className={`${className} ${containerSizeClassName} pointer-events-none rounded bg-[rgba(10,6,5,0.64)] backdrop-blur-md border border-white/14 text-left text-white shadow-sm`}>
      <div className={`font-mono ${labelClassName} uppercase text-white/56 leading-none`}>
        {label}
      </div>
      <div className={`font-mono ${timeClassName} leading-none tabular-nums`}>
        {minutes}:{seconds.toString().padStart(2, '0')}
      </div>
      <div className={poseNameClassName}>
        {poseState.poseName || 'Pose'}
      </div>
    </div>
  );
}
